import { Component, ElementRef, Inject, Injector, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, Attendance, BANT_FACT_LABEL, BANT_FACTS, BantFact, Card, CardDiscardReasonEnum, CardQualification, CardService, CardTemperatureEnum, Comment, CommentService, ConfirmationComponent, Contact, ContactService, Customer, CustomerService, DISCARD_REASON_LABEL, FirebaseService, Pager, PRIORITY_LABEL, Product, ProductService, QUALIFICATION_LABEL, Tag, TagService, Task, TASK_OUTCOME_LABEL, TaskOutcomeEnum, TaskPriorityEnum, TasksService, TaskStatusEnum, TEMPERATURE_LABEL, Timeline, TimelineActionEnum, TimelineService, TimelineTypeEnum, UploadTypeEnum, User, UserService, taskTypeLabelKey } from 'lib-trend-core';
import { BehaviorSubject, finalize, takeUntil } from 'rxjs';
import { Attachments } from '../card-form/card-form.component';
import { ProductQuickFormComponent } from '../product-quick-form/product-quick-form.component';
import { ContactNewConversationComponent } from '../../attendance/attendance-panel/attendance-panel-components/new-conversation/new-conversation.component';
import { NextActionDialogComponent } from '../next-action-dialog/next-action-dialog.component';
import { ConcludeActivityDialogComponent, ConcludeActivityDialogResult } from '../../task/activity-dialogs/conclude-activity-dialog/conclude-activity-dialog.component';
import { PostponeActivityDialogComponent } from '../../task/activity-dialogs/postpone-activity-dialog/postpone-activity-dialog.component';
import { ScoreExplanationComponent } from '../score-explanation/score-explanation.component';
import { CustomerFormModalComponent } from '../customer/customer-form-modal/customer-form-modal.component';
interface CommentWithReplies extends Comment {
  replies?: Comment[];
}
@Component({
    selector: 'app-card-details',
    templateUrl: './card-details.component.html',
    styleUrl: './card-details.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CardDetailsComponent extends AbstractComponent {
  @ViewChild('commentTextarea') commentTextarea?: ElementRef<HTMLTextAreaElement>;
  public comments: Pager<Comment> = new Pager<Comment>({ perPage: 5 });
  public organizedComments = signal<CommentWithReplies[]>([]);
  public replyingTo = signal<string | null>(null);
  public editingComment = signal<Comment | null>(null);
  public timelineData = signal<Array<Timeline>>([]);
  public downloadingFileId = signal<string | null>(null);
  /**
   * The deal's tags — read from the LEAD, never from the contact
   * (owner decision 2026-08-21).
   *
   * This signal used to be filled from `Contact.tags`. It is not the same list: a lead
   * can hold more than one contact, so a tag sitting on one of its phone numbers says
   * nothing about the deal. The attendance panel keeps writing `Contact.tags` for its
   * own purposes and the two are now independent, sharing only the `Tag` catalogue.
   */
  public leadTags = signal<Array<Tag>>([]);

  /** The tenant's tag catalogue, for the add menu. */
  public allTags = signal<Array<Tag>>([]);
  public savingTag = signal<boolean>(false);

  /** Free-text filter of the add menu, so a long catalogue stays usable. */
  public tagSearchControl: FormControl = new FormControl('');
  public tagSearch = signal<string>('');

  /**
   * The full Contact, loaded by `loadContactDetail()`.
   *
   * `Card.contact` arrives as a bare ObjectId — the deal populate list is
   * `user product customer stage funnel lossReason attendance`, and `customer` is
   * populated only one level deep — so anything that needs contact FIELDS (the
   * call button's phone) must read this instead of `data.contact`.
   */
  public contactDetail = signal<Contact | null>(null);

  public editingDescription = false;
  public descriptionDraft = '';
  public users: User[] = [];

  public editingProducts = false;
  public products: Array<Product> = [];
  public listProduct: Array<Product> = [];
  public productControl: FormControl = new FormControl('');
  public productSelectControl: FormControl<Array<Product>> = new FormControl<Array<Product>>([], { nonNullable: true });

  public readonly priorities = [
    { value: TaskPriorityEnum.HIGH, label: 'task.enum.priorityHigh', badgeClass: 'bg-warning' },
    { value: TaskPriorityEnum.MEDIUM, label: 'task.enum.priorityMedium', badgeClass: 'bg-info' },
    { value: TaskPriorityEnum.LOW, label: 'task.enum.priorityLow', badgeClass: 'bg-success' },
    { value: TaskPriorityEnum.CRITICAL, label: 'task.enum.priorityCritical', badgeClass: 'bg-danger' },
    { value: TaskPriorityEnum.NONE, label: 'task.enum.priorityNone', badgeClass: 'bg-secondary' },
  ];

  private loadingSpinnerSubject = new BehaviorSubject<boolean>(false);
  loadingSpinner$ = this.loadingSpinnerSubject.asObservable();

  public file: File;
  public fileLoaded = false;

  constructor(
    injector: Injector,
    public dialogRef: MatDialogRef<CardDetailsComponent>,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: Card,
    private timelineService: TimelineService,
    private commentService: CommentService,
    private cardService: CardService,
    private userService: UserService,
    private productService: ProductService,
    private contactService: ContactService,
    private customerService: CustomerService,
    private tagService: TagService,
    private tasksService: TasksService,
    public firebaseService: FirebaseService,
  ) {
    super(injector);
    this.setupForm();
  }


  /**
   * Daily-radar section of the deal detail
   * (SPEC-2026-08-crm-pipeline-deals-radar).
   *
   * The board card answers "which deal is on fire"; the detail is where the
   * salesperson acts on it, so the same signals plus the deal's activity log belong
   * here. `GET /api/task/card/:idCard` was built with T-23 and had no consumer until
   * this section existed.
   */
  activities: Array<Task> = [];
  loadingActivities = false;

  private getActivities(): void {
    if (!this.data?._id) return;

    this.loadingActivities = true;
    this.tasksService
      .listByCard(this.data._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (activities) => {
          this.activities = activities ?? [];
          this.loadingActivities = false;
        },
        error: () => {
          this.loadingActivities = false;
          this.activities = [];
        },
      });
  }

  /** Opens the scheduling dialog, then refreshes both the deal and its activity log. */
  openNextActionDialog(chainedFromOutcome = false): void {
    const dialogRef = this.dialog.open(NextActionDialogComponent, {
      data: { card: this.data, chainedFromOutcome },
      width: '640px',
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((saved) => {
      if (!saved) return;
      this.getActivities();
      this.refreshCard();
    });
  }

  openScoreExplanation(): void {
    this.dialog.open(ScoreExplanationComponent, { data: { card: this.data }, width: '720px' });
  }

  /**
   * E3 RF-01/RF-07 — the qualification decision and the checklist, edited in place.
   * The maps are exposed so the template pipes keys, never literals (RNF-07).
   */
  readonly QUALIFICATION_LABEL = QUALIFICATION_LABEL;
  readonly BANT_FACT_LABEL = BANT_FACT_LABEL;
  readonly qualificationLevels = Object.keys(QUALIFICATION_LABEL) as Array<CardQualification>;
  readonly bantFacts = BANT_FACTS;
  savingQualification = false;
  savingBant = false;

  get isClosedDeal(): boolean {
    return this.data?.won !== null && this.data?.won !== undefined;
  }

  get qualificationDecided(): CardQualification | undefined {
    return this.data?.qualificationManual?.level;
  }

  get bantDone(): number {
    return BANT_FACTS.filter((fact) => this.data?.bant?.[fact] === true).length;
  }

  /** E3 RF-27: why the deal was lost — the company's reason by name, or the platform discard translated (ADR-001). */
  get lossReasonLabel(): string {
    const key = this.data?.lossReasonKey as CardDiscardReasonEnum | undefined;
    if (key) return this.t(DISCARD_REASON_LABEL[key] ?? key);
    return this.data?.lossReason?.name ?? '';
  }

  /** `null` clears the decision, so the board falls back to the recommendation (RF-02). */
  setQualification(level: CardQualification | null): void {
    if (this.savingQualification || (level ?? undefined) === this.qualificationDecided) return;

    this.savingQualification = true;
    this.cardService.setQualification(this.data._id, level)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.savingQualification = false)))
      .subscribe({
        next: (card) => {
          this.data.qualificationManual = card?.qualificationManual;
          this.alertService.success(this.t('kanban.cardDetails.qualificationSaved'));
        },
        // A refused promotion (BANT below 4/4) arrives translated from the API (RF-08).
        error: (err) => this.alertService.error(err?.error?.message),
      });
  }

  toggleBant(fact: BantFact): void {
    if (this.savingBant) return;

    this.savingBant = true;
    this.cardService.setBant(this.data._id, { [fact]: this.data?.bant?.[fact] !== true })
      .pipe(takeUntil(this.destroy$), finalize(() => (this.savingBant = false)))
      .subscribe({
        next: (card) => (this.data.bant = card?.bant),
        error: (err) => this.alertService.error(err?.error?.message),
      });
  }

  /**
   * Asks what the interaction produced and WHEN, then records it (RF-38) — chaining into
   * the following action when the fulfilled task was the deal's next one (RF-40, AC-21).
   *
   * The picker is a dialog rather than an inline dropdown: the dropdown was clipped by the
   * modal's overflow ancestors, and it could never ask for the moment, which RF-38
   * explicitly allows to be in the past.
   */
  /**
   * E4 (SPEC-2026-10-crm-activities-cadence RF-17): a deal activity is concluded through the ONE
   * conclusion dialog — outcome, moment, duration, notes, an optional move and the next step, in
   * one submit. The dialog re-reads the deal itself and closes with the concluded activity.
   */
  openConclude(task: Task): void {
    const dialogRef = this.dialog.open(ConcludeActivityDialogComponent, {
      data: { task, card: this.data },
      width: '720px',
      disableClose: true,
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: ConcludeActivityDialogResult | undefined) => {
      if (!result) return;
      this.getActivities();
      this.refreshCard();
    });
  }

  /** E4 RF-24: a new moment with a reason, counted on the activity. */
  openPostpone(task: Task): void {
    const dialogRef = this.dialog.open(PostponeActivityDialogComponent, {
      data: { task },
      width: '560px',
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((postponed: Task | undefined) => {
      if (!postponed) return;
      this.getActivities();
      this.refreshCard();
    });
  }

  /** Only an open activity with a scheduled moment can be postponed (RF-24). */
  canPostpone(task: Task): boolean {
    return !this.isConcluded(task) && !!task?.deadlineAt;
  }

  postponementCount(task: Task): number {
    return task?.postponements?.length ?? 0;
  }

  /** The moment the activity was FIRST scheduled for (RF-26): the first postponement's origin. */
  originalMoment(task: Task): Date | null {
    const first = task?.postponements?.[0];
    return first?.from ? new Date(first.from) : null;
  }

  /** `d MMM y` for an untimed moment, with the time otherwise (RF-05). */
  momentFormat(task: Task): string {
    return task?.untimed === true ? 'd MMM y' : 'd MMM y, HH:mm';
  }

  /**
   * The score, the urgency and the last interaction are all recomputed server-side, so
   * the dialog re-reads the deal instead of patching fields locally.
   */
  private refreshCard(): void {
    if (!this.data?._id) return;

    this.cardService
      .getById(this.data._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (card) => {
          if (card) Object.assign(this.data, card);
        },
        // A failed refresh leaves the dialog showing what it already had.
        error: () => undefined,
      });
  }

  activityTypeLabel(task: Task): string {
    return taskTypeLabelKey(task?.type);
  }

  activityOutcomeLabel(task: Task): string {
    return TASK_OUTCOME_LABEL[task?.outcome] ?? 'kanban.taskOutcome.other';
  }

  isConcluded(task: Task): boolean {
    return !!task?.outcome || task?.status === TaskStatusEnum.DONE;
  }

  /**
   * The timeline icon and colour key off the ACTION, not off Portuguese words in
   * the description. `description.includes('criada')` only ever matched because
   * every entry was persisted in pt-BR; now that entries render in the reader's
   * language (RNF-07) an English or Spanish reader would lose the icon entirely.
   */
  isCreateEntry(item: Timeline): boolean {
    return item?.action === TimelineActionEnum.CREATE;
  }

  isStageEntry(item: Timeline): boolean {
    return item?.action === TimelineActionEnum.STAGE;
  }

  ngOnInit(): void {
    this.getTimeline();
    this.getActivities();
    this.getComments();
    this.loadUsers();
    this.loadProducts();
    this.loadContactDetail();
    this.loadLeadTags();
    this.loadAllTags();

    this.productControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((term: string) => this.applyProductFilter(term ?? ''));

    this.tagSearchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((term: string) => this.tagSearch.set(term ?? ''));

    if (this.data.product && this.data.product.length > 0) {
      this.data.product as Array<Product>;
    }
    this.descriptionDraft = this.data?.description;

    console.log('Detalhes do cartão carregados:', this.data);
  }

  /**
   * The lead this deal points at, wherever it happens to be reachable from.
   *
   * Three sources, in order of how complete they are: the document this dialog fetched,
   * the deal's own denormalised `contact`, and the lead behind the customer. That last
   * fallback is the one that matters — `Card.customer` is optional and, on a deal
   * created from the CRM rather than from a conversation, the lead is ONLY reachable
   * through `Card -> Customer -> Contact`.
   *
   * The softphone used to read this chain directly and synchronously
   * (`data.contact ?? data.customer?.contact`). Pointing it at `contactDetail()` alone
   * made the VoIP button depend on an HTTP round-trip that starts with an early return
   * when no id resolves, and whose subscribe has no error branch — so the call option
   * was missing on open, and permanently missing whenever that request never landed.
   * Same reason "Iniciar Atendimento" was gated on `data.contact` alone: that field is
   * written by the recompute, so a deal it had not reached yet offered neither option.
   */
  get leadContact(): Contact | null {
    const candidates = [this.contactDetail(), this.data?.contact, this.data?.customer?.contact];

    // Only a real document counts. `Card.contact` and `Customer.contact` are refs that
    // this payload does not populate, so they arrive as ObjectId STRINGS — an id is
    // enough for `loadContactTags` to fetch with, and not enough to start a
    // conversation or to dial.
    return (candidates.find(c => !!c && typeof c === 'object' && !!(c as Contact)._id) as Contact) ?? null;
  }

  /**
   * Whatever carries a number the softphone can dial (RF — VoIP channel).
   *
   * Deliberately WIDER than `leadContact`: the widget reads only `phone`, `name` and
   * `image`, and never the id — `call()` passes a plain `{ name, phone, image }`. So a
   * deal whose lead is only a Customer can still be called, and that is not a corner
   * case: the sidebar prints `customer.phone` two rows below this button, and a panel
   * that shows a number while the call button is missing is the bug being fixed here.
   *
   * The permission gate is untouched and stays inside the widget: it renders only when
   * the company has the VoIP module AND the logged user has an active extension
   * (`getModuleTrendVoip() && hasRamal`). This only makes sure the number reaches it.
   */
  get callableContact(): Contact | null {
    if (this.leadContact?.phone) return this.leadContact;

    const customer = this.data?.customer;
    if (customer?.phone) {
      return { name: customer.name, phone: customer.phone } as Contact;
    }

    return null;
  }

  /**
   * Fetches the contact document, for everything that needs contact FIELDS.
   *
   * It no longer reads `contact.tags`: the deal's tags come from the lead. See
   * `loadLeadTags()` and the `leadTags` signal.
   */
  private loadContactDetail(): void {
    const contact = this.data?.contact ?? this.data?.customer?.contact;
    const idContact = typeof contact === 'string' ? contact : contact?._id;

    if (!idContact) return;

    this.contactService.getById(idContact)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (contact: Contact) => this.contactDetail.set(contact ?? null),
      });
  }

  /**
   * The lead reached from the deal, when there is one.
   *
   * `Card.customer` is optional and the deal form creates with an empty customer, so a
   * deal can legitimately have no lead — and then it has nowhere to persist a tag. The
   * template uses this to show the tag row as unavailable instead of offering an action
   * that could not be saved.
   */
  get idLead(): string | null {
    const customer = this.data?.customer;
    const id = typeof customer === 'string' ? customer : customer?._id;
    return id ? `${id}` : null;
  }

  // ---------------------------------------------------------------------------
  // E2 — the account behind the deal (SPEC-2026-09-crm-lead-rich-registration)
  // ---------------------------------------------------------------------------

  readonly CardTemperatureEnum = CardTemperatureEnum;

  /** Derived by the recompute (RF-22) — never edited here; "—" while unavailable. */
  get temperatureLabel(): string {
    const temperature = this.data?.temperature as CardTemperatureEnum | undefined;
    return temperature && TEMPERATURE_LABEL[temperature]
      ? TEMPERATURE_LABEL[temperature]
      : 'kanban.cardDetails.temperatureUnavailable';
  }

  /** Segment and size of the account, on one line. */
  get companySummary(): string {
    return [this.data?.customer?.segment, this.data?.customer?.employeeBand].filter(Boolean).join(' · ');
  }

  /** The company block shows when any of its fields is filled — the account has no person-or-company kind. */
  get hasCompanyBlock(): boolean {
    const customer = this.data?.customer;
    return !!(customer?.tradeName || customer?.legalName || customer?.website || customer?.instagram || customer?.segment || customer?.employeeBand);
  }

  /** CPF or CNPJ, by the digits the account holds. */
  get documentLabel(): string {
    const digits = `${this.data?.customer?.tin ?? ''}`.replace(/\D/g, '');
    if (!digits) return '';
    return digits.length > 11 ? 'CNPJ' : 'CPF';
  }

  /**
   * The lead modal, opened from the deal so the deal-level dates (presentation,
   * inclusion) are editable alongside the account (spec A-10). Everything the modal
   * may have touched is reloaded when it closes with a save.
   */
  openEditLead(): void {
    if (!this.idLead) return;

    this.dialog
      .open(CustomerFormModalComponent, { width: '1000px', data: { customer: this.data.customer, card: this.data } })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((saved: boolean) => {
        if (!saved) return;
        this.refreshCard();
        this.loadContactDetail();
        this.loadLeadTags();
      });
  }

  /**
   * Loads the lead's tags with their titles and colours — the PRIMARY CONTACT's tags
   * (E2 RF-27 · ADR-001), which the account read resolves in one round trip.
   *
   * A dedicated read rather than a nested populate on the deal: the board reads hundreds
   * of cards per page and none of them shows tags, so resolving them on every card read
   * would buy a join nothing renders.
   */
  private loadLeadTags(): void {
    const idLead = this.idLead;
    if (!idLead) return;

    this.customerService.getById(idLead)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (customer: Customer) => {
          this.leadTags.set(customer?.contact?.tags ?? []);
          // Keeps the dialog's own copy of the account in step with the fresh read.
          if (customer && this.data) this.data.customer = customer;
        },
      });
  }

  /** The tag catalogue for the add menu — loaded once, filtered by `selectableTags`. */
  private loadAllTags(): void {
    this.tagService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (tags: Array<Tag>) => this.allTags.set(tags ?? []),
      });
  }

  /**
   * The catalogue minus what is already on the lead, narrowed by the search field.
   *
   * The attendance panel lists every tag in its select, where picking an applied one
   * silently removes it. An explicit "add" menu should not offer what is already there —
   * removal has its own affordance on the badge.
   */
  get selectableTags(): Array<Tag> {
    const applied = new Set(this.leadTags().map((tag) => `${tag?._id}`));
    const term = this.tagSearch().trim().toLowerCase();

    return this.allTags().filter((tag) => {
      if (applied.has(`${tag?._id}`)) return false;
      return !term || (tag?.title ?? '').toLowerCase().includes(term);
    });
  }

  /**
   * Keeps typing inside the search field instead of in the menu.
   *
   * `MatMenu` builds its key manager `withTypeAhead()`, so a printable key that reaches
   * the panel moves focus to the `mat-menu-item` whose label matches — the field would
   * lose focus on the first letter. The navigation keys are let through on purpose:
   * Escape still closes the menu and the arrows still walk into the list.
   */
  onTagSearchKeydown(event: KeyboardEvent): void {
    const navigationKeys = ['Escape', 'Tab', 'Enter', 'ArrowDown', 'ArrowUp'];
    if (!navigationKeys.includes(event.key)) event.stopPropagation();
  }

  /** Clears the filter when the menu closes, so it reopens showing the whole catalogue. */
  resetTagSearch(): void {
    this.tagSearchControl.setValue('', { emitEvent: false });
    this.tagSearch.set('');
  }

  /**
   * Adds or removes one tag on the lead (owner decision 2026-08-21).
   *
   * Same single toggle the attendance panel uses, so the badge's remove icon and the add
   * menu are one call. The write lands on the `Customer`; the deal is only the context —
   * which is also why it carries the timeline entry.
   *
   * Closed deals are deliberately NOT blocked, unlike a closed attendance: classifying a
   * lost deal after the fact is normal CRM work (owner decision 2026-08-21).
   */
  addRemoveTag(tag: Tag): void {
    if (!this.data?._id || !tag?._id || this.savingTag()) return;

    this.savingTag.set(true);
    this.cardService.addRemoveTag(this.data._id, `${tag._id}`)
      .pipe(takeUntil(this.destroy$), finalize(() => this.savingTag.set(false)))
      .subscribe({
        next: (contact: Contact) => {
          this.leadTags.set(contact?.tags ?? []);
          // Keeps the dialog's own copy of the primary contact in step, so reopening the
          // row or any sibling read does not show the pre-toggle list.
          if (this.data?.customer?.contact && typeof this.data.customer.contact === 'object') {
            this.data.customer.contact.tags = contact?.tags ?? [];
          }
          this.alertService.success();
        },
        error: (err) => this.alertService.error(err?.error?.message),
      });
  }

  private loadProducts(): void {
    this.productService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list: Array<Product>) => {
          this.products = list;
          this.applyProductFilter(this.productControl.value ?? '');
          this.productSelectControl.setValue(this.resolveSelectedProducts(), { emitEvent: false });
        },
      });
  }

  private resolveSelectedProducts(): Array<Product> {
    const current = (this.data.product ?? []) as Array<Product | string>;
    return current.map((item) => {
      const id = typeof item === 'string' ? item : item._id;
      return this.products.find((p) => p._id === id) ?? (typeof item === 'string' ? { _id: id } as Product : item);
    });
  }

  private applyProductFilter(term: string): void {
    const search = (term ?? '').toString().trim().toLowerCase();
    this.listProduct = !search
      ? [...this.products]
      : this.products.filter((p) => (p.name ?? '').toLowerCase().includes(search));
  }

  compareProduct = (a: Product | string | null, b: Product | string | null): boolean => {
    if (!a || !b) return false;
    const idA = typeof a === 'string' ? a : a._id;
    const idB = typeof b === 'string' ? b : b._id;
    return idA === idB;
  };

  startEditProducts(): void {
    this.productSelectControl.setValue(this.resolveSelectedProducts(), { emitEvent: false });
    this.productSelectControl.markAsPristine();
    this.editingProducts = true;
  }

  cancelEditProducts(): void {
    this.productSelectControl.setValue(this.resolveSelectedProducts(), { emitEvent: false });
    this.productSelectControl.markAsPristine();
    this.editingProducts = false;
  }

  saveProducts(): void {
    const idsProducts = this.productSelectControl.value.map(p => p._id)
    const products = this.productSelectControl.value
    const budget = this.calculateProductTotal(products);
    this.cardService.addRemoveProductToCard(this.data._id, idsProducts)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.data.product = products;
          this.data.budget = budget;
          this.editingProducts = false;
          this.productSelectControl.markAsPristine();
          this.alertService.success(this.t('kanban.cardDetails.productsUpdatedSuccess'));
        },
      })
  }

  removeProduct(product: Product): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '350px',
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;

        const next = (this.productSelectControl.value ?? [])
          .filter((p) => p._id !== product._id);

        this.productSelectControl.setValue(next, { emitEvent: false });
        this.saveProducts();
      });
  }

  private calculateProductTotal(products: Array<Product>): number {
    return products
      .map((p) => p?.price ?? 0)
      .reduce((a, b) => a + b, 0);
  }

  onAttachmentsSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selected = input.files?.[0];
    if (!selected) return;

    this.file = selected;
    this.loadingSpinnerSubject.next(true);

    const filename = new Date().getTime().toString();
    this.firebaseService.uploadFile(selected, filename, UploadTypeEnum.TASK)
      .then((snapshot: { url: string }) => this.persistAttachment(selected, snapshot.url))
      .catch(() => {
        this.loadingSpinnerSubject.next(false);
        this.alertService.error(this.t('kanban.cardDetails.uploadError'));
      });
  }

  removeAttachment(attachment: Attachments) {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '350px',
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.loadingSpinnerSubject.next(true);
      this.cardService.removeAttachment(this.data._id, attachment)
        .pipe(takeUntil(this.destroy$), finalize(() => this.loadingSpinnerSubject.next(false)))
        .subscribe({
          next: (card: Card) => {
            this.data.attachments = card.attachments;
            this.alertService.success(this.t('kanban.cardDetails.attachmentRemovedSuccess'));
          },
          error: () => this.alertService.error(this.t('kanban.cardDetails.attachmentRemoveError')),
        });
    });
  }

  private persistAttachment(file: File, url: string): void {
    const attachment: Attachments = {
      filename: file.name,
      contentType: file.type,
      size: file.size,
      url,
    };

    this.cardService.addAttachment(this.data._id, attachment)
      .pipe(takeUntil(this.destroy$), finalize(() => this.loadingSpinnerSubject.next(false)))
      .subscribe({
        next: (card: Card) => {
          this.data.attachments = card.attachments;
          this.fileLoaded = true;
          this.alertService.success(this.t('kanban.cardDetails.attachmentAddedSuccess'));
        },
        error: () => this.alertService.error(this.t('kanban.cardDetails.attachmentSaveError')),
      });
  }

  createProduct(): void {
    const dialogRef = this.dialog.open(ProductQuickFormComponent, {
      width: '600px',
      data: { companyId: super.getCompanyCurrentUser()._id },
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((created: Product) => {
      if (!created) return;
      this.productService.getList().pipe(takeUntil(this.destroy$)).subscribe({
        next: (list: Array<Product>) => {
          this.products = list;
          this.applyProductFilter(this.productControl.value ?? '');
          const next = [...(this.productSelectControl.value ?? []), created];
          this.productSelectControl.setValue(next, { emitEvent: false });
          this.saveProducts();
        },
      });
    });
  }

  private setupForm(): void {
    this.formGroup = this.formBuilder.group({
      comment: [null],
      author: [super.getIDCurrentUser()],
    });
  }

  private getTimeline() {
    this.loadingContent = true;
    this.timelineService.getTimeline(this.data._id, TimelineTypeEnum.OPPORTUNITY)
      .pipe(takeUntil(this.destroy$), finalize(() => this.loadingContent = false))
      .subscribe({
        next: (response: Array<Timeline>) => {
          this.timelineData.set(response);
        },
        error: (err) => this.alertService.error(this.t('kanban.cardDetails.historyLoadError') + (err?.message ? (this.t('kanban.cardDetails.detailsPrefix') + err.message) : '')),
      });
  }

  private getComments() {
    this.searchParams = { card: this.data._id, };
    this.commentService.getByIdCard(this.comments.page, this.comments.perPage, this.searchString, this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: Pager<Comment>) => {
          if (this.comments.page === 1) {
            this.comments = response;
          } else {
            this.comments.list = [...this.comments.list, ...response.list];
            this.comments.page = response.page;
            this.comments.total = response.total;
          }
          this.organizeComments(this.comments.list);
        },
      });
  }

  startEditDescription() {
    this.descriptionDraft = this.data?.description || '';
    this.editingDescription = true;
  }

  cancelDescription() {
    this.descriptionDraft = '';
    this.editingDescription = false;
  }

  private loadUsers(): void {
    this.userService.findByAttendant()
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (users) => this.users = users });
  }

  updatePriority(priority: string): void {
    this.cardService.updateCardPriority(this.data._id, priority)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.data.priority = priority;
          this.alertService.success(this.t('kanban.cardDetails.priorityUpdatedSuccess'));
        },
        error: () => this.alertService.error(this.t('kanban.cardDetails.priorityUpdateError')),
      });
  }

  updatePrivacy(isPrivate: boolean): void {
    this.cardService.updateCardPrivacy(this.data._id, isPrivate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.data.isPrivate = isPrivate;
          const state = isPrivate ? this.t('kanban.cardDetails.privacyStatePrivate') : this.t('kanban.cardDetails.privacyStatePublic');
          this.alertService.success(this.t('kanban.cardDetails.privacyUpdatedSuccess', { state }));
        },
        error: () => this.alertService.error(this.t('kanban.cardDetails.privacyUpdateError')),
      });
  }

  updateResponsible(user: User | null): void {
    this.cardService.updateCardResponsibel(this.data._id, user)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.data.user = user;
          this.alertService.success(this.t('kanban.cardDetails.responsibleUpdatedSuccess'));
        },
        error: () => this.alertService.error(this.t('kanban.cardDetails.responsibleUpdateError')),
      });
  }

  reply(comment: Comment) {
    const authorName = comment?.author?.name;
    const mention = `@${authorName} `;

    this.replyingTo.set(comment._id);
    this.formGroup.patchValue({ comment: mention });

    setTimeout(() => {
      if (this.commentTextarea) {
        const textarea = this.commentTextarea.nativeElement;
        textarea.focus();
        textarea.setSelectionRange(mention.length, mention.length);
      }
    }, 100);
  }

  cancelReply() {
    this.replyingTo.set(null);
    this.formGroup.patchValue({ comment: '' });
  }

  cancelEdit(): void {
    this.editingComment.set(null);
    this.formGroup.reset();
    this.formGroup.patchValue({ author: super.getIDCurrentUser() });
  }

  saveComment(): void {
    this.loadingContent = true;
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.loadingContent = false;
      return;
    }

    const rowValue = this.formGroup.getRawValue();
    if (!rowValue.comment || rowValue.comment.trim() === '') {
      this.loadingContent = false;
      return;
    }

    const editingId = this.editingComment()?._id;
    const replyingToId = this.replyingTo();

    if (editingId) {
      this.commentService.update(editingId, { content: rowValue.comment })
        .pipe(takeUntil(this.destroy$), finalize(() => this.loadingContent = false))
        .subscribe({
          next: () => {
            this.formGroup.reset();
            this.formGroup.patchValue({ author: super.getIDCurrentUser() });
            this.editingComment.set(null);
            this.replyingTo.set(null);
            this.getComments();
            this.alertService.success(this.t('kanban.cardDetails.commentUpdatedSuccess'));
          },
          error: (err) => this.alertService.error(this.t('kanban.cardDetails.commentUpdateError')),
        });
    }
    else if (replyingToId) {
      const replyData = {
        content: rowValue.comment,
        mentions: [],
        attachments: []
      };

      this.commentService.replyComment(replyingToId, replyData)
        .pipe(takeUntil(this.destroy$), finalize(() => this.loadingContent = false))
        .subscribe({
          next: () => {
            this.formGroup.reset();
            this.formGroup.patchValue({ author: super.getIDCurrentUser() });
            this.replyingTo.set(null);
            this.getComments();
            this.alertService.success(this.t('kanban.cardDetails.replySavedSuccess'));
          },
          error: (err) => this.alertService.error(this.t('kanban.cardDetails.replySaveError')),
        });
    }
    else {
      const comment = {
        content: rowValue.comment,
        card: this.data._id,
        author: rowValue.author
      };

      this.commentService.create(comment)
        .pipe(takeUntil(this.destroy$), finalize(() => this.loadingContent = false))
        .subscribe({
          next: () => {
            this.formGroup.reset();
            this.formGroup.patchValue({ author: super.getIDCurrentUser() });
            this.getComments();
            this.alertService.success(this.t('kanban.cardDetails.commentSavedSuccess'));
          },
          error: (err) => this.alertService.error(this.t('kanban.cardDetails.commentSaveError')),
        });
    }
  }

  deleteComment(comment: Comment): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '350px',
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result === true) {
        this.loadingContent = true;
        this.commentService.delete(comment._id)
          .pipe(takeUntil(this.destroy$), finalize(() => this.loadingContent = false))
          .subscribe({
            next: () => {
              this.alertService.success(this.t('kanban.cardDetails.commentDeletedSuccess'));
              this.getComments();
            },
            error: (err) => this.alertService.error(this.t('kanban.cardDetails.commentDeleteError')),
          });
      }
    });
  }

  editComment(comment: Comment): void {
    this.editingComment.set(comment);
    this.replyingTo.set(null);
    this.formGroup.patchValue({ comment: comment.content });

    setTimeout(() => {
      if (this.commentTextarea) {
        const textarea = this.commentTextarea.nativeElement;
        textarea.focus();
        textarea.select();
      }
    }, 100);
  }

  handleKeydown(event: KeyboardEvent): void {
    const messageControl = this.formGroup.get('comment');

    if (event.key === '@' && !messageControl?.value) {
      event.preventDefault();
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.saveComment();
    }
  }

  loadPage(page: number) {
    this.comments.page = page;
    this.getComments();
  }

  downloadFile(attachment: Attachments): void {
    if (!attachment?.url || !this.isValidUrl(attachment.url)) {
      this.alertService.error(this.t('kanban.cardDetails.invalidFileError'));
      return;
    }

    const filename = attachment.filename?.trim();
    if (!filename) {
      this.alertService.error(this.t('kanban.cardDetails.invalidFilenameError'));
      return;
    }

    try {
      this.downloadingFileId.set(attachment.url);
      fetch(attachment.url)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          return response.blob();
        })
        .then(blob => {
          this.createDownloadLink(blob, filename);
          this.alertService.success(this.t('kanban.cardDetails.downloadStartedSuccess'));
        })
        .catch(() => {
          this.alertService.error(this.t('kanban.cardDetails.downloadError'));
        })
        .finally(() => {
          this.downloadingFileId.set(null);
        });
    } catch (error) {
      this.downloadingFileId.set(null);
      this.alertService.error(this.t('kanban.cardDetails.downloadProcessError'));
    }
  }

  translateEnuns(value: string) {
    return PRIORITY_LABEL[value as TaskPriorityEnum] ?? 'kanban.cardDetails.priorityLabel';
  }

  navigateToAttendance(card: Card): void {
    if (!!card.attendance) {
      this.router.navigate(['/attendance/panel'], { queryParams: { id: card.attendance?._id, status: card.attendance?.status } });
    }

    this.close();
  }

  startNewChat(card: Card): void {
    const dialogRef = this.dialog.open(ContactNewConversationComponent, {
      width: '90vw',
      height: '95vh',
      // Same resolution the button's own guard uses, so it can never open the
      // conversation dialog without the lead it was enabled for.
      data: { contact: this.leadContact ?? card.contact },
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((attendance: Attendance) => {
        if (!attendance?._id) return;

        this.cardService.linkAttendance(this.data._id, attendance._id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.data = { ...this.data, attendance };
              this.navigateToAttendance(this.data);
            },
            error: (err) => this.alertService.error(err.error.message),
          });
      });

  }


  close(): void {
    this.dialogRef.close();
  }

  getTimeAgo(date: string | Date) {
    const now = new Date();
    const past = new Date(date);
    const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return `${diffInSeconds} seg atrás`;
    }
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} min atrás`;
    }
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} horas atrás`;
    }
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} dias atrás`;
  }

  private organizeComments(comments: Comment[]): void {
    const commentsMap = new Map<string, CommentWithReplies>();
    const rootComments: CommentWithReplies[] = [];

    comments.forEach(comment => {
      const withReplies: CommentWithReplies = { ...comment, replies: [] };
      commentsMap.set(comment._id, withReplies);

      if (!comment.parentComment) {
        rootComments.push(withReplies);
      }
    });

    comments.forEach(comment => {
      if (comment.parentComment) {
        const parent = commentsMap.get(comment.parentComment as string);
        if (parent) {
          parent.replies.push(comment);
        }
      }
    });

    rootComments.forEach(root => {
      if (root.replies.length > 0) {
        root.replies.sort((a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      }
    });

    this.organizedComments.set(rootComments);
  }

  private isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  private createDownloadLink(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  }

  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';

    const KB = 1024;
    const MB = KB * 1024;
    const GB = MB * 1024;
    const TB = GB * 1024;

    if (bytes < KB) return `${bytes} B`;
    if (bytes < MB) return `${(bytes / KB).toFixed(2)} KB`;
    if (bytes < GB) return `${(bytes / MB).toFixed(2)} MB`;
    if (bytes < TB) return `${(bytes / GB).toFixed(2)} GB`;

    return `${(bytes / TB).toFixed(2)} TB`;
  }

  isImage(url: string): boolean {
    if (!url) return false;

    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
    const lowerUrl = url.toLowerCase();

    return imageExtensions.some(ext => lowerUrl.includes(ext));
  }

  isPDF(url: string): boolean {
    if (!url) return false;

    return url.toLowerCase().includes('.pdf');
  }

  saveDescription(): void {
    this.loadingContent = true;
    this.cardService.update(this.data._id, { ...this.data, description: this.descriptionDraft })
      .pipe(takeUntil(this.destroy$), finalize(() => this.loadingContent = false))
      .subscribe({
        next: () => {
          this.data.description = this.descriptionDraft;
          this.editingDescription = false;
          this.alertService.success(this.t('kanban.cardDetails.descriptionUpdatedSuccess'));
        },
        error: () => this.alertService.error(this.t('kanban.cardDetails.descriptionUpdateError')),
      });
  }
}
